import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllFees, updateFeeStatus } from '../../../redux/feeRelated/feeHandle';
import {
    Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography, Chip, Tooltip, Card,
    Grid, Avatar, LinearProgress, FormControl, InputLabel, Select, MenuItem, Button,
    Container, Stack, Divider, useTheme, useMediaQuery,
    Dialog, DialogTitle, DialogContent, DialogActions, Menu,
    TextField, InputAdornment
} from '@mui/material';
import styled, { keyframes } from 'styled-components';
import {
    Visibility as VisibilityIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Payment as PaymentIcon,
    FileDownload as FileDownloadIcon,
    Add as AddIcon,
    AttachMoney as AttachMoneyIcon,
    AccountBalance as AccountBalanceIcon,
    History as HistoryIcon,
    TrendingUp as TrendingUpIcon,
    FilterList as FilterListIcon,
    Wallet as WalletIcon,
    ReceiptLong as ReceiptLongIcon,
    Print as PrintIcon,
    Class as ClassIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { useToast } from '../../../context/ToastContext';
import { underControl } from '../../../redux/feeRelated/feeSlice';
import {
    exportToExcel
} from '../../../utils/excelExport';
import FeeReceipt from './FeeReceipt';
import { getNepaliMonthOptions, formatDatePart, getCurrentBSDate, formatToBSDate } from '../../../utils/customDate';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const getFeeFinancials = (fee = {}) => {
    const cashPaid = Number(fee.paidAmount) || 0;
    const advancePaid = Number(fee.advanceUsed) || 0;
    const payableAmount = Number(fee.amount) || 0;
    const grossAmount = Number(fee.grossAmount) || Math.max(0, payableAmount + advancePaid);
    const collected = cashPaid + advancePaid;
    const due = Math.max(0, payableAmount - cashPaid);

    return {
        cashPaid,
        advancePaid,
        payableAmount,
        grossAmount,
        collected,
        due
    };
};

const ShowAllFees = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { currentUser } = useSelector((state) => state.user);
    const { feesList, loading, response, status } = useSelector((state) => state.fee);

    const { showToast } = useToast();
    const [selectedClass, setSelectedClass] = useState("");
    const [selectedMonth, setSelectedMonth] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [viewMode, setViewMode] = useState('grouped');

    // Status change dialog state
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [feePendingStatusChange, setFeePendingStatusChange] = useState(null);
    const [newStatusPending, setNewStatusPending] = useState('');


    // Print receipt state
    const [printOpen, setPrintOpen] = useState(false);
    const [printFee, setPrintFee] = useState(null);

    // Export dropdown state
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const isExportMenuOpen = Boolean(exportAnchorEl);

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    useEffect(() => {
        if (currentUser) {
            dispatch(getAllFees(currentUser._id));
        }
    }, [currentUser, dispatch]);

    useEffect(() => {
        if (status === "added") {
            dispatch(underControl());
            dispatch(getAllFees(currentUser._id));
        }
    }, [status, dispatch, currentUser._id]);

    const handleToggleStatus = (fee) => {
        if (!fee || !fee._id) return;

        // Cycle: Paid -> Partial -> Unpaid -> Paid
        const current = (fee.status || '').toLowerCase();
        const nextStatus = current === 'paid'
            ? 'Partial'
            : current === 'partial'
                ? 'Unpaid'
                : 'Paid';

        // For Partial we store paidAmount as advanceUsed (if available),
        // otherwise half of amount (fallback).
        const amount = Number(fee.amount) || 0;
        const advance = Number(fee.advanceUsed) || 0;
        const computedPartialPaid = advance > 0 ? advance : amount > 0 ? amount / 2 : 0;

        const data = {
            month: fee.month,
            status: nextStatus,
            amount,
            paidAmount:
                nextStatus === 'Paid' ? amount :
                nextStatus === 'Partial' ? computedPartialPaid :
                0,
            paymentDate:
                nextStatus === 'Paid' || nextStatus === 'Partial'
                    ? getCurrentBSDate()
                    : null,
        };

        dispatch(updateFeeStatus(fee._id, data)).then((res) => {
            if (res && res.success) {
                showToast(`Status updated to ${nextStatus}`, "success");
            }
        });
    };




    const openStatusDialog = (fee) => {
        console.log('OPEN STATUS DIALOG clicked', fee);
        if (!fee) return;

        const current = (fee.status || '').toLowerCase();
        const newStatus = current === 'paid'
            ? 'Partial'
            : current === 'partial'
                ? 'Unpaid'
                : 'Paid';

        setFeePendingStatusChange(fee);
        setNewStatusPending(newStatus);
        setStatusDialogOpen(true);
    };


    const openStatusDialogWithStop = (e, fee) => {
        console.log('CLICKED STATUS CHIP handler', fee);
        // Stop immediate row handlers.
        if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        openStatusDialog(fee);
    };





    const handleConfirmStatusChange = () => {
        if (!feePendingStatusChange) {
            setStatusDialogOpen(false);
            return;
        }

        // Confirm only: do the status update here (not on chip click)
        handleToggleStatus(feePendingStatusChange);

        setStatusDialogOpen(false);
        setFeePendingStatusChange(null);
        setNewStatusPending('');
    };



    const getFlattenedFees = () => {
        if (!feesList || !Array.isArray(feesList)) return [];

        // Do not filter out the entire student record even if they have a pending draft,
        // otherwise their entire fee history disappears.
        const nonDraftFeesList = feesList;

        let flattened = [];

        nonDraftFeesList.forEach((fee) => {
            if (fee.feeDetails && fee.feeDetails.length > 0) {
                fee.feeDetails.forEach((detail) => {
                    // Exclude draft fee details that have no bill number
                    if (fee.billStatus === 'draft' && detail.billNumber == null) {
                        return;
                    }
                    flattened.push({
                        ...detail,
                        studentId: fee.student?._id,
                        studentName: fee.student?.name || 'Unknown',
                        rollNum: fee.student?.rollNum || 'N/A',
                        className: (() => {
                            const classRef = fee.student?.sclassName;
                            const classId = (typeof classRef === 'object') ? classRef?._id : classRef;
                            // Note: ShowAllFees doesn't have sclassesList in state, 
                            // but we can try to get it from the populated student object first
                            return fee.student?.sclassName?.sclassName || classId || 'N/A';
                        })(),
                        classId: fee.student?.sclassName?._id || '',
                        feeId: fee._id
                    });
                });
            }
        });

        if (searchQuery.trim() !== "") {
            const query = searchQuery.toLowerCase();
            flattened = flattened.filter(fee => 
                fee.studentName.toLowerCase().includes(query) ||
                (fee.billNumber && String(fee.billNumber).toLowerCase().includes(query))
            );
        } else {
            if (selectedClass) flattened = flattened.filter(fee => fee.classId === selectedClass);
            if (selectedMonth) {
                flattened = flattened.filter(fee => {
                    const monthLabel = getNepaliMonthOptions().find(m => m.value === selectedMonth)?.label;
                    const matchesLabel = monthLabel && fee.month && fee.month.includes(monthLabel);
                    return matchesLabel;
                });
            }
        }
        // Sort by createdAt descending so latest records are shown first
        flattened.sort((a, b) => {
            const dateA = new Date(a.createdAt || 0);
            const dateB = new Date(b.createdAt || 0);
            return dateB - dateA;
        });

        return flattened;
    };

    const fees = getFlattenedFees();

    const getGroupedFees = (flattenedFees) => {
        const groups = {};
        flattenedFees.forEach(fee => {
            const dateStr = fee.createdAt ? formatToBSDate(fee.createdAt) : 'Unknown Date';
            if (!groups[dateStr]) {
                groups[dateStr] = {
                    date: dateStr,
                    records: [],
                    totalAmount: 0,
                    totalPaid: 0
                };
            }
            groups[dateStr].records.push(fee);
            const financials = getFeeFinancials(fee);
            groups[dateStr].totalAmount += financials.grossAmount;
            groups[dateStr].totalPaid += financials.collected;
        });
        
        return Object.values(groups).sort((a, b) => {
            if (a.date === 'Unknown Date') return 1;
            if (b.date === 'Unknown Date') return -1;
            return b.date.localeCompare(a.date);
        });
    };

    const calculateSummary = () => {
        let expected = 0;
        let collected = 0;
        let pendingCount = 0;
        const studentTotals = {};
        
        fees.forEach(fee => {
            const financials = getFeeFinancials(fee);
            const expectedForFee = financials.grossAmount;

            expected += expectedForFee;
            collected += financials.collected;
            if (financials.due > 0) pendingCount += 1;

            if (!studentTotals[fee.studentId]) {
                studentTotals[fee.studentId] = { expected: 0, collected: 0 };
            }
            studentTotals[fee.studentId].expected += expectedForFee;
            studentTotals[fee.studentId].collected += financials.collected;
        });

        let outstanding = 0;
        Object.values(studentTotals).forEach(totals => {
            const studentOutstanding = totals.expected - totals.collected;
            if (studentOutstanding > 0) {
                outstanding += studentOutstanding;
            }
        });

        return { expected, collected, outstanding, count: fees.length, pendingCount };
    };

    const summary = calculateSummary();
    const collectionRate = summary.expected > 0 ? (summary.collected / summary.expected * 100).toFixed(1) : 0;

    const getUniqueClasses = () => {
        const classes = new Map();
        feesList.forEach(fee => {
            if (fee.student?.sclassName?._id) {
                classes.set(fee.student.sclassName._id, fee.student.sclassName.sclassName);
            }
        });
        return Array.from(classes.entries()).map(([id, name]) => ({ id, name }));
    }; 

    const handleExportStatement = (statementType) => {
        if (!feesList || !Array.isArray(feesList)) {
            showToast("No data available to export", "warning");
            return;
        }

        // Get all flattened fees
        let flattened = [];
        feesList.forEach((fee) => {
            if (fee.feeDetails && fee.feeDetails.length > 0) {
                fee.feeDetails.forEach((detail) => {
                    // Exclude draft bills from the statement.
                    // A detail is a draft if its parent document is in 'draft' state and the detail lacks a bill number.
                    if (fee.billStatus === 'draft' && detail.billNumber == null) {
                        return;
                    }

                    flattened.push({
                        ...detail,
                        studentId: fee.student?._id,
                        studentName: fee.student?.name || 'Unknown',
                        rollNum: fee.student?.rollNum || 'N/A',
                        className: (() => {
                            const classRef = fee.student?.sclassName;
                            const classId = (typeof classRef === 'object') ? classRef?._id : classRef;
                            return fee.student?.sclassName?.sclassName || classId || 'N/A';
                        })(),
                        classId: fee.student?.sclassName?._id || '',
                        feeId: fee._id
                    });
                });
            }
        });

        // Apply class filter if any
        if (selectedClass) {
            flattened = flattened.filter(fee => fee.classId === selectedClass);
        }

        // Get current BS date (e.g. "2083-02-06")
        const todayBS = getCurrentBSDate();
        const [curYear, curMonth, curDay] = todayBS.split('-');

        let filteredFees = [];
        let filename = '';
        let sheetName = '';

        if (statementType === 'daily') {
            filteredFees = flattened.filter(fee => {
                const feeBS = formatToBSDate(fee.paymentDate || fee.createdAt);
                return feeBS === todayBS;
            });
            filename = `Daily_Statement_${todayBS}`;
            sheetName = 'Daily Statement';
        } else if (statementType === 'monthly') {
            const targetMonthPrefix = `${curYear}-${curMonth}`;
            filteredFees = flattened.filter(fee => {
                const feeBS = formatToBSDate(fee.paymentDate || fee.createdAt);
                return feeBS.startsWith(targetMonthPrefix);
            });
            filename = `Monthly_Statement_${targetMonthPrefix}`;
            sheetName = 'Monthly Statement';
        } else if (statementType === 'yearly') {
            const targetYearPrefix = `${curYear}`;
            filteredFees = flattened.filter(fee => {
                const feeBS = formatToBSDate(fee.paymentDate || fee.createdAt);
                return feeBS.startsWith(targetYearPrefix);
            });
            filename = `Yearly_Statement_${targetYearPrefix}`;
            sheetName = 'Yearly Statement';
        }

        if (filteredFees.length === 0) {
            showToast(`No billing records found for the selected ${statementType} statement period`, "info");
            return;
        }

        // Sort filteredFees sequentially by billNumber (ascending)
        filteredFees.sort((a, b) => {
            const valA = a.billNumber;
            const valB = b.billNumber;
            if (valA === null || valA === undefined || valA === '') {
                if (valB === null || valB === undefined || valB === '') return 0;
                return 1;
            }
            if (valB === null || valB === undefined || valB === '') {
                return -1;
            }
            const numA = Number(valA);
            const numB = Number(valB);
            if (isNaN(numA) || isNaN(numB)) {
                return String(valA).localeCompare(String(valB));
            }
            return numA - numB;
        });

        const getRemainingDues = (f) => {
            return getFeeFinancials(f).due;
        };

        const data = filteredFees.map(f => {
            const financials = getFeeFinancials(f);
            return {
                'Student ID': f.studentId || '-',
                'Student': f.studentName,
                'Roll': f.rollNum,
                'Class': f.className,
                'Bill No': f.billNumber || '-',
                'Month': f.month,
                'Amount': financials.grossAmount,
                'Discount': f.discount || 0,
                'Paid': financials.cashPaid,
                'Advance Used': financials.advancePaid,
                'Collected': financials.collected,
                'Dues': getRemainingDues(f),
                'Status': f.status,
                'Bill Date (BS)': formatToBSDate(f.createdAt),
                'Payment Date (BS)': f.paymentDate ? formatToBSDate(f.paymentDate) : '-',
                'Description': (() => {
                let desc = f.description || '';
                const dues = Number(f.duesAmount) || 0;
                const disc = Number(f.discount) || 0;
                if (dues > 0) {
                    const duesText = `Dues: Rs. ${dues}`;
                    if (!desc.toLowerCase().includes('dues') || !desc.includes(String(dues))) {
                        desc = desc ? `${desc}, - ${duesText}` : duesText;
                    }
                }
                if (disc > 0) {
                    const discText = `Discount: Rs. ${disc}`;
                    if (!desc.toLowerCase().includes('discount') || !desc.includes(String(disc))) {
                        desc = desc ? `${desc}, - ${discText}` : discText;
                    }
                }
                const advance = Number(f.advanceUsed) || 0;
                if (advance > 0) {
                    const advanceText = `Advance Used: Rs. ${advance}`;
                    if (!desc.toLowerCase().includes('advance used') || !desc.includes(String(advance))) {
                        desc = desc ? `${desc}, - ${advanceText}` : advanceText;
                    }
                }
                return desc || '-';
            })(),
            };
        });

        // Calculate totals
        const totalBills = filteredFees.length;
        const totalAmount = filteredFees.reduce((sum, f) => sum + getFeeFinancials(f).grossAmount, 0);
        const totalDiscount = filteredFees.reduce((sum, f) => sum + (f.discount || 0), 0);
        const totalAdvance = filteredFees.reduce((sum, f) => sum + getFeeFinancials(f).advancePaid, 0);
        const totalDues = filteredFees.reduce((sum, f) => sum + getRemainingDues(f), 0);
        const totalPaid = filteredFees.reduce((sum, f) => sum + getFeeFinancials(f).cashPaid, 0);
        const totalCollected = filteredFees.reduce((sum, f) => sum + getFeeFinancials(f).collected, 0);

        // Add empty row for spacing
        data.push({
            'Student ID': '',
            'Student': '',
            'Roll': '',
            'Class': '',
            'Bill No': '',
            'Month': '',
            'Amount': '',
            'Discount': '',
            'Paid': '',
            'Advance Used': '',
            'Collected': '',
            'Dues': '',
            'Status': '',
            'Bill Date (BS)': '',
            'Payment Date (BS)': '',
            'Description': '',
         });

        // Add summary row with total bill count
        let labelText = '';
        if (statementType === 'daily') {
            labelText = 'Total Bill Count (Daily)';
        } else if (statementType === 'monthly') {
            labelText = 'Total Bill Count (Monthly)';
        } else {
            labelText = 'Total Bill Count (Yearly)';
        }

        data.push({
            'Student ID': '',
            'Student': labelText,
            'Roll': totalBills,
            'Class': '',
            'Bill No': '',
            'Month': 'Totals:',
            'Amount': totalAmount,
            'Discount': totalDiscount,
            'Paid': totalPaid,
            'Advance Used': totalAdvance,
            'Collected': totalCollected,
            'Dues': totalDues,
            'Status': '',
            'Bill Date (BS)': '',
            'Payment Date (BS)': '',
            'Description': '',
         });

        exportToExcel(data, filename, sheetName);
        showToast(`${sheetName} exported successfully`, "success");
    };

    const getStatusStyles = (status) => {
        switch (status) {
            case 'Paid': return { bg: '#e8f5e9', color: '#2e7d32', icon: <CheckCircleIcon fontSize="small" /> };
            case 'Partial': return { bg: '#fff3e0', color: '#ed6c02', icon: <HistoryIcon fontSize="small" /> };
            case 'Unpaid': return { bg: '#ffebee', color: '#d32f2f', icon: <WarningIcon fontSize="small" /> };
            default: return { bg: '#f5f5f5', color: '#757575', icon: <HistoryIcon fontSize="small" /> };
        }
    };

    if (loading && feesList.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
                <LinearProgress sx={{ width: '200px', mb: 2, borderRadius: '10px' }} />
                <Typography color="textSecondary">Fetching financial records...</Typography>
            </Box>
        );
    }

    return (
        <PageContainer>
            <HeaderWrapper elevation={0}>
                <Container maxWidth="xl">
                    <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} spacing={2} sx={{ py: 4 }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: 'white', mb: 1 }}>
                                Fee Management
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Manage and track student financial contributions
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate("/Admin/addfee")}
                                sx={{ bgcolor: 'white', color: '#1a1a2e', '&:hover': { bgcolor: '#f0f0f0' }, borderRadius: '12px', px: 3, fontWeight: 700 }}
                            >
                                New Entry
                            </Button>
                            <Button 
                            startIcon={<FileDownloadIcon />}
                                onClick={handleExportClick}
                                 sx={{ bgcolor: 'white', color: '#1a1a2e', '&:hover': { bgcolor: '#f0f0f0' }, borderRadius: '12px', px: 3, fontWeight: 700 }}
                                >
                                <Typography variant='button'>Statement</Typography> 
                                 
                            </Button>
                            <Menu
                                anchorEl={exportAnchorEl}
                                open={isExportMenuOpen}
                                onClose={handleExportClose}
                                PaperProps={{
                                    sx: {
                                        borderRadius: '16px',
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                                        border: '1px solid rgba(0, 0, 0, 0.05)',
                                        mt: 1,
                                        '& .MuiMenuItem-root': {
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            py: 1.5,
                                            px: 3,
                                            '&:hover': {
                                                bgcolor: '#f1f5f9'
                                            }
                                        }
                                    }
                                }}
                            >
                                <MenuItem onClick={() => { handleExportStatement('daily'); handleExportClose(); }}>
                                    Daily Statement
                                </MenuItem>
                                <MenuItem onClick={() => { handleExportStatement('monthly'); handleExportClose(); }}>
                                    Monthly Statement
                                </MenuItem>
                                <MenuItem onClick={() => { handleExportStatement('yearly'); handleExportClose(); }}>
                                    Yearly Statement
                                </MenuItem>
                            </Menu>
                        </Stack>
                    </Stack>
                </Container>
            </HeaderWrapper>

            <Container maxWidth="xl" sx={{ mt: 2, height: 'calc(100vh - 120px)', overflow: 'hidden', pb: 2 }}>
                <Grid container spacing={3} sx={{ height: '100%' }}>
                    {/* Left Sidebar: Class Selection */}
                    <Grid item xs={12} md={3} sx={{ height: '100%' }}>
                        <Box sx={{ height: '100%' }}>
                            <SectionCard elevation={2}>
                                <SectionHeader>
                                    <ClassIcon sx={{ color: '#667eea' }} />
                                    <Typography variant="subtitle1" fontWeight="700">Select Class</Typography>
                                </SectionHeader>
                                <Box sx={{ p: 2, height: 'calc(100% - 60px)', overflowY: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '10px' } }}>
                                    <Stack spacing={1}>
                                        <ClassItem
                                            active={selectedClass === ""}
                                            onClick={() => setSelectedClass("")}
                                            startIcon={<ClassIcon />}
                                        >
                                            All Classes
                                        </ClassItem>
                                        {getUniqueClasses().map(c => (
                                            <ClassItem
                                                key={c.id}
                                                active={selectedClass === c.id}
                                                onClick={() => setSelectedClass(c.id)}
                                                startIcon={<ClassIcon />}
                                            >
                                                {c.name}
                                            </ClassItem>
                                        ))}
                                    </Stack>
                                </Box>
                            </SectionCard>
                        </Box>
                    </Grid>

                    {/* Right Side: Main Content */}
                    <Grid item xs={12} md={9} sx={{ height: '100%', overflowY: 'auto', pr: 1, '&::-webkit-scrollbar': { width: '6px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: '10px' } }}>
                        <Grid container spacing={3}>
                            {/* Summary Cards */}
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard elevation={2}>
                            <IconBox color="#667eea"><WalletIcon /></IconBox>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>Expected Total</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800 }}>Rs.{(summary.expected || 0).toLocaleString()}</Typography>
                            </Box>
                        </StatCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard elevation={2}>
                            <IconBox color="#4caf50"><CheckCircleIcon /></IconBox>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>Collected</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#4caf50' }}>Rs.{(summary.collected || 0).toLocaleString()}</Typography>
                            </Box>
                        </StatCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard elevation={2}>
                            <IconBox color={summary.outstanding > 0 ? "#f44336" : "#4caf50"}>
                                {summary.outstanding > 0 ? <WarningIcon /> : <CheckCircleIcon />}
                            </IconBox>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>Outstanding</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: summary.outstanding > 0 ? '#f44336' : '#4caf50' }}>
                                    Rs.{(summary.outstanding || 0).toLocaleString()}
                                </Typography> 
                            </Box>
                        </StatCard>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatCard elevation={2}>
                            <IconBox color="#ff9800"><TrendingUpIcon /></IconBox>
                            <Box sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 600 }}>Collection Rate</Typography>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{collectionRate}%</Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={parseFloat(collectionRate)}
                                    sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: '#f1f5f9',
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: parseFloat(collectionRate) > 80 ? '#4caf50' : parseFloat(collectionRate) > 40 ? '#ff9800' : '#f44336'
                                        }
                                    }}
                                />
                            </Box>
                        </StatCard>
                    </Grid>

                    {/* Filters */}
                    <Grid item xs={12}>
                        <FilterPaper elevation={1}>
                            <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center" sx={{ p: 2 }}>
                                <FilterListIcon sx={{ color: 'text.secondary', display: isMobile ? 'none' : 'block' }} />
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel>Filter by Month</InputLabel>
                                    <Select value={selectedMonth} label="Filter by Month" onChange={(e) => setSelectedMonth(e.target.value)}>
                                        <MenuItem value="">All Months</MenuItem>
                                        {getNepaliMonthOptions().map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 155 }}>
                                    <InputLabel>Group Records</InputLabel>
                                    <Select value={viewMode} label="Group Records" onChange={(e) => setViewMode(e.target.value)}>
                                        <MenuItem value="grouped">Grouped by Date</MenuItem>
                                        <MenuItem value="continuous">Continuous List</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    placeholder="Search by name or bill no..."
                                    size="small"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: 'text.secondary' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchQuery && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setSearchQuery('')}
                                                    edge="end"
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        minWidth: isMobile ? '100%' : 280,
                                        bgcolor: 'white',
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '12px'
                                        }
                                    }}
                                />
                                <Box sx={{ flexGrow: 1 }} />
                                <Typography variant="caption" color="textSecondary">
                                    Displaying {fees.length} records
                                </Typography>
                            </Stack>
                        </FilterPaper>
                    </Grid>

                    {/* Main Table */}
                    <Grid item xs={12}>
                        <StyledTableContainer component={Paper} elevation={2}>
                            <Table sx={{ minWidth: 900 }}>
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <StyledTableCell>Student Info</StyledTableCell>
                                        <StyledTableCell>Class & Month</StyledTableCell>
                                        <StyledTableCell>Financials</StyledTableCell>
                                        <StyledTableCell>Status</StyledTableCell>
                                        <StyledTableCell>Payment Date</StyledTableCell>
                                        <StyledTableCell align="center">Actions</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {fees.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                                <ReceiptLongIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                                                <Typography variant="body1" color="textSecondary" sx={{ fontWeight: 600 }}>
                                                    No billing records found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {viewMode === 'grouped' ? (
                                        getGroupedFees(fees).map((group) => (
                                            <React.Fragment key={group.date}>
                                                <TableRow>
                                                    <TableCell colSpan={6} sx={{ 
                                                        p: 0,
                                                        bgcolor: '#f8fafc',
                                                        borderBottom: '1px solid #e2e8f0'
                                                    }}>
                                                        <Box sx={{ 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'space-between',
                                                            py: 1.5, 
                                                            px: 3, 
                                                            borderLeft: '4px solid #667eea',
                                                            background: 'linear-gradient(90deg, #eff6ff 0%, rgba(255,255,255,0) 100%)'
                                                        }}>
                                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                                <CalendarTodayIcon sx={{ color: '#667eea', fontSize: 18 }} />
                                                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                                                    {group.date}
                                                                </Typography>
                                                                <Chip 
                                                                    label={`${group.records.length} ${group.records.length === 1 ? 'Record' : 'Records'}`} 
                                                                    size="small" 
                                                                    sx={{ 
                                                                        bgcolor: '#667eea', 
                                                                        color: 'white', 
                                                                        fontWeight: 700,
                                                                        fontSize: '0.75rem',
                                                                        height: 20
                                                                    }} 
                                                                />
                                                            </Stack>
                                                            <Stack direction="row" spacing={3} alignItems="center">
                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                                                                    Total Billed: <span style={{ color: '#1e293b', fontWeight: 800 }}>Rs.{group.totalAmount.toLocaleString()}</span>
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                                                                    Collected: <span style={{ color: '#16a34a', fontWeight: 800 }}>Rs.{group.totalPaid.toLocaleString()}</span>
                                                                </Typography>
                                                            </Stack>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                                {group.records.map((fee, idx) => {
                                                    const styles = getStatusStyles(fee.status);
                                                    const financials = getFeeFinancials(fee);
                                                    return (
                                                        <TableRow key={`${group.date}-${idx}`} hover sx={{ transition: '0.2s', '&:hover': { bgcolor: '#f1f5f9' } }}>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={2} alignItems="center">
                                                                    <Avatar sx={{ bgcolor: '#667eea', fontWeight: 700 }}>{fee.studentName[0]}</Avatar>
                                                                    <Box>
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            sx={{ fontWeight: 700, cursor: 'pointer', color: '#1a73e8' }}
                                                                            onClick={() =>
                                                                                navigate('/Admin/addfee', {
                                                                                    state: {
                                                                                        studentId: fee.studentId,
                                                                                        classId: fee.classId,
                                                                                        className: fee.className,
                                                                                        from: 'feesList',
                                                                                        mode: 'prefill',
                                                                                        studentName: fee.studentName,
                                                                                    },
                                                                                })
                                                                            }
                                                                        >
                                                                            {fee.studentName}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="textSecondary">Roll: {fee.rollNum}</Typography>
                                                                    </Box>
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box>
                                                                    <Chip label={fee.className} size="small" sx={{ fontWeight: 600, mb: 0.5 }} />
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{fee.month}</Typography>
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Billed: Rs.{financials.grossAmount.toLocaleString()}</Typography>
                                                                    <Typography variant="caption" sx={{ display: 'block', color: '#16a34a', fontWeight: 800 }}>Collected: Rs.{financials.collected.toLocaleString()}</Typography>
                                                                    {financials.cashPaid > 0 && (
                                                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Cash Paid: Rs.{financials.cashPaid.toLocaleString()}</Typography>
                                                                    )}
                                                                    {financials.advancePaid > 0 && (
                                                                        <Typography variant="caption" sx={{ display: 'block', color: '#2563eb', fontWeight: 700 }}>Advance Paid: Rs.{financials.advancePaid.toLocaleString()}</Typography>
                                                                    )}
                                                                    {financials.due > 0 && (
                                                                        <Typography variant="caption" color="error" sx={{ display: 'block', fontWeight: 700 }}>Due: Rs.{financials.due.toLocaleString()}</Typography>
                                                                    )}
                                                                    {fee.discount > 0 && (
                                                                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>Discount: Rs.{fee.discount.toLocaleString()}</Typography>
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Tooltip title="Click to change status">
                                                                    <StatusChip
                                                                        label={fee.status}
                                                                        onClick={(e) => openStatusDialogWithStop(e, fee)}
                                                                        icon={styles.icon}
                                                                        sx={{ bgcolor: styles.bg, color: styles.color, fontWeight: 700 }}
                                                                    />
                                                                </Tooltip>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box>
                                                                    {fee.paymentDate && (
                                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                                            <CheckCircleIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                                                                            <Typography variant="caption" sx={{ color: '#4caf50' }}>{formatDatePart(fee.paymentDate)}</Typography>
                                                                        </Stack>
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Stack direction="row" spacing={1} justifyContent="center">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => navigate(`/Admin/students/student/fee/${fee.studentId}`, { state: { highlightMonth: fee.month } })}
                                                                        sx={{ color: '#667eea', '&:hover': { bgcolor: '#f0f4ff' } }}
                                                                    >
                                                                        <VisibilityIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <Tooltip title="Print Receipt">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => { setPrintFee(fee); setPrintOpen(true); }}
                                                                            sx={{ color: '#ff9800', '&:hover': { bgcolor: '#fff3e0' } }}
                                                                        >
                                                                            <PrintIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        fees.map((fee, idx) => {
                                            const styles = getStatusStyles(fee.status);
                                            const financials = getFeeFinancials(fee);
                                            return (
                                                <TableRow key={idx} hover sx={{ transition: '0.2s', '&:hover': { bgcolor: '#f1f5f9' } }}>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                            <Avatar sx={{ bgcolor: '#667eea', fontWeight: 700 }}>{fee.studentName[0]}</Avatar>
                                                            <Box>
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    sx={{ fontWeight: 700, cursor: 'pointer', color: '#1a73e8' }}
                                                                    onClick={() =>
                                                                        navigate('/Admin/addfee', {
                                                                            state: {
                                                                                studentId: fee.studentId,
                                                                                classId: fee.classId,
                                                                                className: fee.className,
                                                                                from: 'feesList',
                                                                                mode: 'prefill',
                                                                                studentName: fee.studentName,
                                                                            },
                                                                        })
                                                                    }
                                                                >
                                                                    {fee.studentName}
                                                                </Typography>
                                                                <Typography variant="caption" color="textSecondary">Roll: {fee.rollNum}</Typography>
                                                            </Box>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            <Chip label={fee.className} size="small" sx={{ fontWeight: 600, mb: 0.5 }} />
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{fee.month}</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Billed: Rs.{financials.grossAmount.toLocaleString()}</Typography>
                                                            <Typography variant="caption" sx={{ display: 'block', color: '#16a34a', fontWeight: 800 }}>Collected: Rs.{financials.collected.toLocaleString()}</Typography>
                                                            {financials.cashPaid > 0 && (
                                                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>Cash Paid: Rs.{financials.cashPaid.toLocaleString()}</Typography>
                                                            )}
                                                            {financials.advancePaid > 0 && (
                                                                <Typography variant="caption" sx={{ display: 'block', color: '#2563eb', fontWeight: 700 }}>Advance Paid: Rs.{financials.advancePaid.toLocaleString()}</Typography>
                                                            )}
                                                            {financials.due > 0 && (
                                                                <Typography variant="caption" color="error" sx={{ display: 'block', fontWeight: 700 }}>Due: Rs.{financials.due.toLocaleString()}</Typography>
                                                            )}
                                                            {fee.discount > 0 && (
                                                                <Typography variant="caption" color="error" sx={{ display: 'block' }}>Discount: Rs.{fee.discount.toLocaleString()}</Typography>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip title="Click to change status">
                                                            <StatusChip
                                                                label={fee.status}
                                                                onClick={(e) => openStatusDialogWithStop(e, fee)}
                                                                icon={styles.icon}
                                                                sx={{ bgcolor: styles.bg, color: styles.color, fontWeight: 700 }}
                                                            />
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            {fee.paymentDate && (
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    <CheckCircleIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                                                                    <Typography variant="caption" sx={{ color: '#4caf50' }}>{formatDatePart(fee.paymentDate)}</Typography>
                                                                </Stack>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Stack direction="row" spacing={1} justifyContent="center">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => navigate(`/Admin/students/student/fee/${fee.studentId}`, { state: { highlightMonth: fee.month } })}
                                                                sx={{ color: '#667eea', '&:hover': { bgcolor: '#f0f4ff' } }}
                                                            >
                                                                <VisibilityIcon fontSize="small" />
                                                            </IconButton>
                                                            <Tooltip title="Print Receipt">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => { setPrintFee(fee); setPrintOpen(true); }}
                                                                    sx={{ color: '#ff9800', '&:hover': { bgcolor: '#fff3e0' } }}
                                                                >
                                                                    <PrintIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                        </Grid>
                    </Grid>
                </Grid>
                </Grid>
            </Container>

            {/* Status Change Confirmation Dialog */}
            <Dialog
                open={statusDialogOpen}
                onClose={() => setStatusDialogOpen(false)}
                PaperProps={{
                    sx: { borderRadius: '24px', p: 2, minWidth: 420 }
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 900, color: '#1e293b' }}>
                    <WarningIcon color="warning" /> Confirm Status Change
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ color: '#64748b', fontWeight: 500, fontSize: '1.1rem' }}>
                        Are you sure you want to change status?
                        {newStatusPending ? (
                            <Typography component="span" sx={{ fontWeight: 800, color: '#1e293b', ml: 0.5 }}>
                                ({newStatusPending})
                            </Typography>
                        ) : null}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button
                        onClick={() => {
                            setStatusDialogOpen(false);
                            setFeePendingStatusChange(null);
                            setNewStatusPending('');
                        }}
                        variant="outlined"
                        sx={{ borderRadius: '12px', fontWeight: 700, color: '#64748b', borderColor: '#cbd5e1', textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmStatusChange}
                        variant="contained"
                        sx={{ borderRadius: '12px', fontWeight: 700, textTransform: 'none' }}
                        disableElevation
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Print Receipt - reuses shared FeeReceipt component */}
            <FeeReceipt

                open={printOpen}
                onClose={() => setPrintOpen(false)}
                feeData={printFee}
            />
        </PageContainer>
    );
};

export default ShowAllFees;

const PageContainer = styled.div`background: #f8fafc; min-height: 100vh; animation: ${fadeIn} 0.5s ease-out;`;
const HeaderWrapper = styled(Paper)`
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    border-radius: 0 0 40px 40px !important;
    padding-bottom: 40px;
`;
const StatCard = styled(Paper)`
    padding: 24px; border-radius: 20px !important; display: flex; align-items: center; gap: 16px;
    transition: transform 0.3s ease; &:hover { transform: translateY(-5px); }
`;
const IconBox = styled.div`
    width: 50px; height: 50px; border-radius: 14px; display: flex; align-items: center; justify-content: center;
    background: ${props => `${props.color}15`}; color: ${props => props.color};
    & svg { font-size: 28px; }
`;
const FilterPaper = styled(Paper)`border-radius: 16px !important; overflow: hidden;`;
const StyledTableContainer = styled(TableContainer)`border-radius: 20px !important; overflow: hidden;`;
const StyledTableCell = styled(TableCell)`font-weight: 700 !important; color: #475569 !important; padding: 16px !important;`;
const StatusChip = styled(Chip)`height: 28px !important; border-radius: 8px !important; font-size: 0.75rem !important; cursor: pointer;`;

const SectionCard = styled(Paper)`border-radius: 20px !important; overflow: hidden; border: 1px solid #e2e8f0; height: 100%; display: flex; flex-direction: column;`;
const SectionHeader = styled(Box)`padding: 12px 24px; background: #f8fafc; color: #1e293b; display: flex; align-items: center; gap: 12px; border-bottom: 1px solid #e2e8f0;`;

const ClassItem = styled(Button)`
    width: 100%;
    justify-content: flex-start !important;
    text-align: left !important;
    padding: 12px 16px !important;
    margin-bottom: 8px !important;
    border-radius: 12px !important;
    text-transform: none !important;
    font-weight: 600 !important;
    color: ${props => props.active ? '#fff' : '#64748b'} !important;
    background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent'} !important;
    border: 1px solid ${props => props.active ? 'transparent' : '#e2e8f0'} !important;
    box-shadow: ${props => props.active ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none'} !important;
    transition: all 0.2s ease !important;

    &:hover {
        background: ${props => props.active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f1f5f9'} !important;
        transform: translateX(4px);
    }

    svg {
        margin-right: 12px;
        color: ${props => props.active ? '#fff' : '#667eea'};
    }
`;
